import {Popover, Box, Overlay} from '@mantine/core';
import {FC, useEffect, useState, useMemo} from 'react';
import {useDisclosure} from '@mantine/hooks';
import {shallowEqual} from 'react-redux';

import {useDispatch, useSelector} from '~/store/hooks';
import useMediaQuery from '~/hooks/useMediaQuery';
import {TNotification} from '~/services/hot-api/notifications';
import Icon, {Icons} from '~/common/Icon';
import Button from '~/common/Button/Button';
import Loader from '~/common/Loader/Loader';
import {useNotifications} from '~/common/Notifications';

import {NotificationButton, NotificationModal, NotificationsList} from './parts';
import s from './styles.module.scss';

interface NotificationsProps {
  buttonClassName?: string;
}

const Notifications: FC<NotificationsProps> = ({buttonClassName}) => {
  const dispatch = useDispatch();
  const {response} = useSelector((state) => state.profile.notifications, shallowEqual);
  const {isLoadingMarkAll, isLoadingDeleteAll} = useSelector(
    (state) => ({
      isLoadingMarkAll: state.loading.effects.profile.setAllNotifactionsAsReaded.loading,
      isLoadingDeleteAll: state.loading.effects.profile.removeAllNotifications.loading,
    }),
    shallowEqual
  );
  const {showNotification, hasNotifications} = useNotifications();

  const isMobile = useMediaQuery('(max-width: 767px)');
  const [notificationOpened, {open, close}] = useDisclosure(false);
  const [selectedNotification, setSelectedNotification] = useState<TNotification | null>(null);

  const [opened, setOpened] = useState(false);

  useEffect(() => {
    dispatch.profile.loadNotifications();
  }, [dispatch]);

  const hasNotificationItems = useMemo(() => response.total !== 0, [response.total]);
  const hasUnreadNotifications = useMemo(() => response.unviewed !== 0, [response.unviewed]);

  const handleDeleteAll = async () => {
    try {
      await dispatch.profile.removeAllNotifications();
    } catch (error) {
      showNotification({
        type: 'error',
        title: 'Error',
        message: 'Oops! Something went wrong. Please try again in a minute.',
        closeTime: 2000,
      });
    }
  };

  const handleMarkAll = async () => {
    try {
      await dispatch.profile.setAllNotifactionsAsReaded();
    } catch (error) {
      showNotification({
        title: 'Error',
        type: 'error',
        message: 'Oops! Something went wrong. Please try again in a minute.',
        closeTime: 2000,
      });
    }
  };

  return (
    <Box>
      {opened && <Overlay fixed />}
      <Popover
        position={isMobile ? 'bottom' : 'bottom-end'}
        width="min(100%, 480px)"
        withArrow
        radius="md"
        shadow="md"
        opened={opened}
        onChange={setOpened}
        zIndex={1000}
        withinPortal
        classNames={{
          dropdown: s['popover-dropdown'],
        }}
        portalProps={{
          target: document.body,
        }}
        arrowOffset={20}
        // to prevent closing when close notifications
        closeOnClickOutside={!hasNotifications}
      >
        <Popover.Target>
          <NotificationButton
            className={buttonClassName}
            onClick={() => setOpened((o) => !o)}
            count={response.unviewed}
          />
        </Popover.Target>
        <Popover.Dropdown>
          <Box className={s.dropdown__wrapper}>
            <div className={s.dropdown__header}>
              <div className={s.dropdown__title}>
                Notifications
                <div className={s['dropdown__header-actions']}>
                  {hasNotificationItems && (
                    <Button
                      onClick={handleDeleteAll}
                      disabled={isLoadingMarkAll}
                      className={s['dropdown__icon-button']}
                      loading={isLoadingDeleteAll}
                      loader={<Loader size={18} />}
                    >
                      <Icon name={Icons.delete} className={s['dropdown__icon-delete']} />
                    </Button>
                  )}
                  <Button onClick={() => setOpened(false)} className={s['dropdown__icon-button']}>
                    <Icon name={Icons.cross} className={s['dropdown__icon-close']} />
                  </Button>
                </div>
              </div>
              {hasUnreadNotifications && (
                <div className={s['dropdown__total-items']}>
                  <span className={s['dropdown__notifications-count']}>
                    {response.unviewed} new notifications.{' '}
                  </span>
                  <Button
                    variant="outline"
                    className={s['dropdown__mark-all-button']}
                    onClick={handleMarkAll}
                    disabled={isLoadingDeleteAll}
                    loading={isLoadingMarkAll}
                    loader={<Loader size={13} />}
                  >
                    Mark all as read
                  </Button>
                </div>
              )}
            </div>
            <NotificationsList
              notifications={response}
              onNotificationOpen={(notify) => {
                setSelectedNotification(notify);
                setOpened(false);
                open();
              }}
              onNotificationAction={() => setOpened(false)}
              onNotificationDelete={(notify) => {
                dispatch.profile.removeNotification(notify.id);
              }}
            />
          </Box>
        </Popover.Dropdown>
      </Popover>
      {selectedNotification && (
        <NotificationModal
          opened={notificationOpened}
          notification={selectedNotification}
          onAction={close}
          onClose={close}
        />
      )}
    </Box>
  );
};

export default Notifications;
